import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, setDoc, getDoc, query, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { PhoneCall, Link2, Copy, X } from 'lucide-react';

/**
 * 관리자: A/S 컨시어지 매칭 (/admin/as-leads)
 * 비회원 치과 신청 → 확인 전화 → 기공소 선정 → 수락 링크 생성·전달
 */
const STATUS = {
    new: { text: '신규', color: '#b91c1c', bg: '#fbeaea' },
    contacted: { text: '전화 완료', color: '#b45309', bg: '#fdf4e7' },
    published: { text: '게시판 게시됨', color: '#1d4ed8', bg: '#e9effc' },
    proposed: { text: '기공소 제안 중', color: '#1d4ed8', bg: '#e9effc' },
    matched: { text: '매칭 완료', color: '#157f3d', bg: '#e9f5ee' },
    closed: { text: '종료', color: '#5b6472', bg: '#eef0f3' },
    spam: { text: '스팸', color: '#8b93a1', bg: '#f0f1f4' },
};
const FLOW = ['new', 'contacted', 'published', 'proposed', 'matched', 'closed', 'spam'];

function AdminAsLeads() {
    const [leads, setLeads] = useState([]);
    const [labs, setLabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('active');
    const [target, setTarget] = useState(null);         // 기공소 연결 대상 리드
    const [selLab, setSelLab] = useState('');
    const [working, setWorking] = useState(false);
    const [linkInfo, setLinkInfo] = useState(null);      // { url, testNumber, labName }
    const [loadError, setLoadError] = useState('');

    const fetchAll = useCallback(async () => {
        try {
            setLoadError('');
            const snap = await getDocs(query(collection(db, 'asLeads'), orderBy('createdAt', 'desc')));
            setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            // 기공소 후보: labAdvertisements(홍보 등록) 기준
            const adSnap = await getDocs(collection(db, 'labAdvertisements'));
            setLabs(adSnap.docs.map(d => ({
                id: d.id,
                name: d.data().businessName || d.data().companyName || d.data().labName || d.id,
                region: d.data().address ? d.data().address.split(' ').slice(0, 2).join(' ') : '',
            })).filter(l => l.name));
        } catch (e) {
            console.error('리드 조회 실패:', e);
            setLoadError(e.code === 'permission-denied' || String(e.message).includes('permission')
                ? '이 화면은 관리자 계정(role: admin)만 볼 수 있습니다. admin 계정으로 다시 로그인해주세요.'
                : '목록을 불러오지 못했습니다: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { fetchAll(); }, [fetchAll]);

    const setStatus = async (lead, status) => {
        try {
            await updateDoc(doc(db, 'asLeads', lead.id), { status, updatedAt: serverTimestamp() });
            setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status } : l));
        } catch (e) { alert('상태 변경 실패: ' + e.message); }
    };
    const saveMemo = async (lead, memo) => {
        try {
            await updateDoc(doc(db, 'asLeads', lead.id), { adminMemo: memo, updatedAt: serverTimestamp() });
        } catch (e) { /* 무시 */ }
    };

    // [확인] — 공개 게시판에 게시 (모든 기공소 선착순 수락, 결제 없음·건당 수동 청구)
    const publishLead = async (lead) => {
        if (!window.confirm(`"${lead.clinicName}" 케이스를 테스트보철 게시판에 올릴까요?\n\n· 모든 기공소가 보고 선착순 수락합니다\n· 수락 시 기공소에게 담당자 번호(010-2972-3733)가 안내됩니다\n· 치과 연락처는 기공소에 공개되지 않습니다`)) return;
        setWorking(true);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'publishLeadCase');
            const res = await fn({ leadId: lead.id });
            alert(`게시 완료 (${res.data.testNumber}) — 기공소 게시판에 노출됩니다.`);
            await fetchAll();
        } catch (e) {
            alert(e.message || '게시 실패');
        } finally {
            setWorking(false);
        }
    };

    const connectLab = async () => {
        if (!selLab) { alert('기공소를 선택해주세요.'); return; }
        setWorking(true);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'createLeadCase');
            const res = await fn({ leadId: target.id, labId: selLab });
            const url = `https://www.dentalcluster.com/test-cases/${res.data.requestId}`;
            const labName = labs.find(l => l.id === selLab)?.name || '';
            setLinkInfo({ url, testNumber: res.data.testNumber, labName, clinicName: target.clinicName });
            setTarget(null); setSelLab('');
            await fetchAll();
        } catch (e) {
            alert(e.message || '케이스 생성 실패');
        } finally {
            setWorking(false);
        }
    };

    const copyText = (t) => { navigator.clipboard?.writeText(t); alert('복사되었습니다.'); };

    const filtered = leads.filter(l =>
        filter === 'all' ? true :
        filter === 'active' ? ['new', 'contacted', 'published', 'proposed'].includes(l.status) :
        l.status === filter);

    const fmtDate = ts => ts?.toDate ? ts.toDate().toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
    const chip = (s) => { const c = STATUS[s] || STATUS.new; return <span style={{ fontSize: 11, fontWeight: 700, color: c.color, background: c.bg, borderRadius: 5, padding: '3px 9px', whiteSpace: 'nowrap' }}>{c.text}</span>; };

    const newCount = leads.filter(l => l.status === 'new').length;

    // 🔗 리드 초대코드 발급 (QR 퍼널 P0) — 치과가 이 코드로 가입하면 파트너십·수수료가 자동 개시

    const issueLeadInvite = async (l) => {

        if (!l.matchedLabId) { alert('매칭된 기공소가 없습니다. 수락(매칭 완료) 후 발급하세요.'); return; }

        let code = Math.random().toString(36).slice(2, 10).toUpperCase();

        try {

            for (let i = 0; i < 3; i++) {

                const ex = await getDoc(doc(db, 'clinicInvites', code));

                if (!ex.exists()) break;

                code = Math.random().toString(36).slice(2, 10).toUpperCase();

            }

            await setDoc(doc(db, 'clinicInvites', code), {

                labId: l.matchedLabId,

                labName: l.matchedLabName || '',

                clinicName: l.clinicName || l.name || '',

                leadId: l.id,

                matchedLabId: l.matchedLabId,

                status: 'active',

                createdAt: Timestamp.now(),

                expiresAt: Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 3600 * 1000)),

                usedBy: null, usedAt: null,

            });

            const url = `${window.location.origin}/signup?invite=${code}`;

            try { await navigator.clipboard.writeText(url); } catch (e) { window.prompt('가입 링크 복사:', url); }

            alert(`초대코드 발급 완료 — 가입 링크가 복사되었습니다.\n\n${url}\n\n치과에 문자로 전달하세요. 이 코드로 가입하면 거래처 연결과 수수료 파트너십이 자동으로 생성됩니다.`);

        } catch (e) {

            console.error('초대코드 발급 실패:', e);

            alert('발급에 실패했습니다.');

        }

    };


    return (
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px' }}>
            <h1 style={{ fontSize: 23, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 9, marginTop: 0, letterSpacing: '-0.4px', flexWrap: 'wrap' }}>
                <PhoneCall size={22} color="#274690" /> A/S 신청 관리
                {newCount > 0 && <span style={{ fontSize: 12, fontWeight: 800, color: '#fff', background: '#b91c1c', borderRadius: 11, padding: '3px 11px' }}>신규 {newCount}</span>}
                <a href="/test-cases" target="_blank" rel="noreferrer"
                    style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #d3d7de', borderRadius: 8, padding: '9px 14px', fontSize: 12.5, fontWeight: 700, color: '#274690', textDecoration: 'none' }}>
                    테스트보철 게시판 열기 ↗
                </a>
            </h1>
            <p style={{ color: '#5b6472', fontSize: 13.5, margin: '4px 0 18px' }}>
                비회원 치과 신청 목록입니다. 확인 전화 → 기공소 연결 → 수락 링크를 기공소에 카톡/문자로 전달하세요.
            </p>

            <div style={{ display: 'flex', gap: 7, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                    ['active', '진행 중', leads.filter(l => ['new', 'contacted', 'published', 'proposed'].includes(l.status)).length],
                    ['matched', '매칭 완료', leads.filter(l => l.status === 'matched').length],
                    ['closed', '종료', leads.filter(l => l.status === 'closed').length],
                    ['all', '전체', leads.length],
                ].map(([k, t, n]) => (
                    <button key={k} onClick={() => setFilter(k)}
                        style={{ padding: '8px 15px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: filter === k ? '1px solid #274690' : '1px solid #e6e8ec', background: filter === k ? '#eef1f8' : '#fff', color: filter === k ? '#1d3568' : '#5b6472' }}>
                        {t} {n > 0 && <b style={{ color: '#274690' }}>{n}</b>}
                    </button>
                ))}
            </div>

            {loadError && (
                <div style={{ background: '#fbeaea', border: '1px solid #f0d9d9', borderLeft: '3px solid #b91c1c', borderRadius: 8, padding: '13px 16px', fontSize: 13.5, color: '#b91c1c', fontWeight: 600, marginBottom: 14 }}>
                    {loadError}
                </div>
            )}
            {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#8b93a1' }}>불러오는 중…</div>
            : filtered.length === 0 ? <div style={{ textAlign: 'center', padding: 60, color: '#8b93a1', background: '#fff', border: '1px solid #e6e8ec', borderRadius: 12 }}>해당하는 신청이 없습니다. 랜딩 주소: dentalcluster.com/as</div>
            : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {filtered.map(l => (
                        <div key={l.id} style={{ background: '#fff', border: '1px solid #e6e8ec', borderRadius: 12, padding: '15px 18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <b style={{ fontSize: 15, letterSpacing: '-0.3px' }}>{l.clinicName}</b>
                                {chip(l.status)}
                                <a href={`tel:${l.phone}`} style={{ fontSize: 13, color: '#274690', fontWeight: 700, textDecoration: 'none' }}>{l.phone?.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3')}</a>
                                <span style={{ fontSize: 12, color: '#8b93a1' }}>{l.region || '지역 미기재'} · {l.prosthesisType}{l.contactName ? ` · ${l.contactName}` : ''}</span>
                                <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#8b93a1' }}>{fmtDate(l.createdAt)}</span>
                            </div>
                            {l.notes && <div style={{ fontSize: 13, color: '#5b6472', marginTop: 7, lineHeight: 1.6 }}>{l.notes}</div>}
                            {(l.assignedLabName || l.testRequestId) && (
                                <div style={{ fontSize: 12.5, color: '#1d4ed8', marginTop: 7, fontWeight: 600 }}>
                                    {l.assignedLabName ? `→ ${l.assignedLabName} ` : ''}{l.matchedLabName && !l.assignedLabName ? `→ ${l.matchedLabName} 수락 ` : ''}{l.testNumber && `(${l.testNumber})`}
                                    {l.status === 'matched' && (
                                        <button onClick={(e) => { e.stopPropagation(); issueLeadInvite(l); }}
                                            style={{ marginLeft: 8, padding: '3px 10px', border: 'none', borderRadius: 6, background: '#6366F1', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                                            🔗 초대코드 발급
                                        </button>
                                    )}
                                    {l.testRequestId && (
                                        <a href={`/test-cases/${l.testRequestId}`} target="_blank" rel="noreferrer"
                                            style={{ marginLeft: 10, border: '1px solid #d3d7de', background: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, color: '#274690', textDecoration: 'none' }}>
                                            게시글 보기 ↗
                                        </a>
                                    )}
                                    {l.status === 'proposed' && l.testRequestId && (
                                        <button onClick={() => copyText(`https://www.dentalcluster.com/test-cases/${l.testRequestId}`)}
                                            style={{ marginLeft: 10, border: '1px solid #d3d7de', background: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', color: '#274690' }}>
                                            <Copy size={11} style={{ verticalAlign: '-1px' }} /> 수락 링크 복사
                                        </button>
                                    )}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, marginTop: 11, flexWrap: 'wrap', alignItems: 'center' }}>
                                <select value={l.status} onChange={e => setStatus(l, e.target.value)}
                                    style={{ border: '1px solid #d3d7de', borderRadius: 7, padding: '7px 10px', fontSize: 12.5, fontFamily: 'inherit' }}>
                                    {FLOW.map(s => <option key={s} value={s}>{STATUS[s].text}</option>)}
                                </select>
                                {['new', 'contacted'].includes(l.status) && (
                                    <>
                                        <button onClick={() => publishLead(l)} disabled={working}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#157f3d', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                            ✓ 확인 (게시판 게시)
                                        </button>
                                        <button onClick={() => { setTarget(l); setSelLab(''); }}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#274690', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                            <Link2 size={13} /> 기공소 직접 지정
                                        </button>
                                    </>
                                )}
                                <input defaultValue={l.adminMemo || ''} placeholder="관리자 메모 (자동 저장)"
                                    onBlur={e => saveMemo(l, e.target.value)}
                                    style={{ flex: 1, minWidth: 180, border: '1px solid #e6e8ec', borderRadius: 7, padding: '8px 11px', fontSize: 12.5, fontFamily: 'inherit', background: '#f8f9fb' }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 기공소 연결 모달 */}
            {target && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(21,27,38,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: 26, maxWidth: 420, width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{target.clinicName} — 기공소 연결</h3>
                            <button onClick={() => setTarget(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b93a1' }}><X size={19} /></button>
                        </div>
                        <p style={{ fontSize: 12.5, color: '#5b6472', margin: '8px 0 16px', lineHeight: 1.6 }}>
                            선택한 기공소만 수락할 수 있는 초대제 케이스가 생성됩니다. 생성된 링크를 기공소에 전달하세요.
                        </p>
                        <select value={selLab} onChange={e => setSelLab(e.target.value)}
                            style={{ width: '100%', border: '1px solid #d3d7de', borderRadius: 9, padding: '12px 12px', fontSize: 14, fontFamily: 'inherit' }}>
                            <option value="">기공소 선택</option>
                            {labs.map(l => <option key={l.id} value={l.id}>{l.name}{l.region ? ` — ${l.region}` : ''}</option>)}
                        </select>
                        <button onClick={connectLab} disabled={working}
                            style={{ width: '100%', marginTop: 14, background: '#274690', color: '#fff', border: 'none', borderRadius: 9, padding: '13px', fontSize: 14.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {working ? '생성 중…' : '케이스 생성 + 수락 링크 만들기'}
                        </button>
                    </div>
                </div>
            )}

            {/* 링크 생성 완료 모달 */}
            {linkInfo && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(21,27,38,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 14, padding: 26, maxWidth: 460, width: '100%' }}>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>수락 링크 생성 완료 · {linkInfo.testNumber}</h3>
                        <p style={{ fontSize: 13, color: '#5b6472', margin: '10px 0 14px', lineHeight: 1.7 }}>
                            아래 링크를 <b>{linkInfo.labName}</b>에 카톡/문자로 보내세요.<br />
                            기공소가 로그인 후 [수락]하면 {linkInfo.clinicName} 연락처가 공개됩니다.
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input readOnly value={linkInfo.url} style={{ flex: 1, border: '1px solid #d3d7de', borderRadius: 8, padding: '11px 12px', fontSize: 12.5, fontFamily: 'ui-monospace, monospace', background: '#f8f9fb' }} />
                            <button onClick={() => copyText(linkInfo.url)}
                                style={{ background: '#274690', color: '#fff', border: 'none', borderRadius: 8, padding: '0 16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>복사</button>
                        </div>
                        <div style={{ background: '#f8f9fb', borderRadius: 9, padding: '11px 13px', fontSize: 12, color: '#5b6472', marginTop: 12, lineHeight: 1.7 }}>
                            보낼 문구 예시: "원장님, 덴탈클러스터입니다. {linkInfo.clinicName} A/S 케이스({linkInfo.testNumber})를 제안드립니다. 아래 링크에서 확인 후 수락해주세요. 첫 건은 무료입니다."
                        </div>
                        <button onClick={() => setLinkInfo(null)}
                            style={{ width: '100%', marginTop: 14, background: '#eef0f3', color: '#374151', border: 'none', borderRadius: 9, padding: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>닫기</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminAsLeads;
